import React from "react";
import { NAV_ITEMS, isActive } from "./Sidebar";
import { useNotificationContext } from "../contexts/NotificationContext";

/**
 * BottomNav - Mobile-only bottom navigation bar
 *
 * Renders a fixed bottom bar with the same 4 nav items as the Sidebar,
 * displayed as icon-only buttons. Only visible on screens below the `md` breakpoint.
 *
 * @param {string} activePath - Current route path for active state highlighting
 * @param {function} onNavigate - Navigation handler called with the target href
 */
export default function BottomNav({ activePath, onNavigate }) {
  const { unreadCount } = useNotificationContext();

  return (
    <nav
      aria-label="Mobile navigation"
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden border-t border-border bg-surface-elevated"
    >
      <ul className="flex items-center justify-around h-16">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href, activePath);

          return (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => onNavigate(item.href)}
                aria-current={active ? "page" : undefined}
                aria-label={item.label}
                className="relative flex flex-col items-center justify-center w-16 h-14 rounded-xl"
              >
                <div className="relative">
                  <Icon
                    className={[
                      "h-6 w-6 transition-colors duration-150",
                      active ? "text-primary" : "text-text-muted",
                    ].join(" ")}
                    aria-hidden="true"
                  />

                  {/* Unread notification dot */}
                  {item.key === "notifications" && unreadCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 inline-block h-2 w-2 rounded-full bg-primary ring-2 ring-surface-elevated"
                      aria-hidden="true"
                    />
                  )}
                </div>

                <span
                  className={[
                    "text-[10px] mt-1 transition-colors duration-150",
                    active
                      ? "text-primary font-semibold"
                      : "text-text-muted",
                  ].join(" ")}
                >
                  {item.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
